/**
 * GenerationPanel - Insole generation parameters and controls
 * 
 * Provides thickness slider, edge profile, offset direction selectors,
 * and a generate button. Shows progress during generation.
 */

import React, { useState } from 'react'
import type { OffsetDirection } from '../../../services/types'
import './GenerationPanel.css'

interface GenerationPanelProps {
  /** Callback to start generation */
  onGenerate: (params: {
    thickness_mm: number
    offset_direction: OffsetDirection
    edge_profile: string
    top_surface: string
  }) => void
  /** Loading state */
  isLoading?: boolean
  /** Cancel generation */
  onCancel?: () => void
}

export const GenerationPanel: React.FC<GenerationPanelProps> = ({
  onGenerate,
  isLoading = false,
  onCancel
}) => {
  const [thickness, setThickness] = useState<number>(3.0)
  const [offsetDirection, setOffsetDirection] = useState<OffsetDirection>('normal')
  // Retained for backend-request compat; hidden from the doctor because the
  // backend ignores this field (post PR #414/#415).
  const [edgeProfile] = useState<string>('chamfer')

  const handleGenerate = () => {
    onGenerate({
      thickness_mm: thickness,
      offset_direction: offsetDirection,
      edge_profile: edgeProfile,
      top_surface: 'draped'
    })
  }

  return (
    <div className="generation-panel">
      <h3>Insole Generation</h3>

      <div className="gen-parameter">
        <label htmlFor="thickness-slider">
          Thickness
          <span className="gen-value">{thickness.toFixed(1)} mm</span>
        </label>
        <input
          id="thickness-slider"
          type="range"
          min="1"
          max="10"
          step="0.5"
          value={thickness}
          onChange={(e) => setThickness(parseFloat(e.target.value))}
          disabled={isLoading}
          className="gen-slider"
        />
        <div className="slider-labels">
          <span>1mm</span>
          <span>5mm</span>
          <span>10mm</span>
        </div>
      </div>

      {/*
        Edge Profile removed from user-facing UI (2026-07-02): the backend
        currently ignores this field. Retained as a hidden state for backward
        compatibility with the generate request shape only.
      */}

      <div className="gen-parameter">
        <label htmlFor="offset-direction-select">Offset Direction</label>
        <select
          id="offset-direction-select"
          value={offsetDirection}
          onChange={(e) => setOffsetDirection(e.target.value as OffsetDirection)}
          disabled={isLoading}
          className="gen-select"
        >
          <option value="normal">Normal</option>
          <option value="z_axis">Z-Axis</option>
        </select>
      </div>

      <div className="gen-actions">
        <button
          onClick={handleGenerate}
          disabled={isLoading}
          className="gen-button"
        >
          {isLoading ? 'Generating...' : 'Generate Insole'}
        </button>
        {isLoading && onCancel && (
          <button onClick={onCancel} className="gen-cancel-button">
            Cancel
          </button>
        )}
      </div>

      {isLoading && (
        <div className="gen-progress">
          <div className="gen-progress-bar">
            <div className="gen-progress-fill" />
          </div>
          <p className="gen-progress-text">Computing insole geometry...</p>
        </div>
      )}
    </div>
  )
}
